import { Box, Button, Divider, IconButton, Typography } from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  ChevronLeft,
  ChevronLeftRounded,
  ChevronRight,
  ChevronRightRounded,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import dagre from "dagre";
import React from "react";
import ReactFlow, { MarkerType, useEdgesState, useNodesState } from "reactflow";
import "reactflow/dist/style.css";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../api/hooks/useSmartAndRegularAssets";
import { entityKeys, scenarioKeys, stepKeys } from "../../../../api/queryKeys";
import CustomTooltip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import iconMapping from "../../../../icons/IconMapping";
import SvgToIcon from "../../../../icons/SvgToIcon";
import {
  ComplexEntityTypes,
  ProtectionLevelText,
  ScenariosModalStateLogic,
} from "../../../../utils/constants";
import { isScenarioEffectivelyMitigated } from "../../../../utils/scenarioUtils";
import { isComplexEntityType } from "../../../../utils/utils";
import EntityDrawer from "../../../stack/environment/components/EntityDrawer/EntityDrawer";
import { useEntities } from "../../../stack/environment/hooks/useEntities";
import { extractTypeFromEntityId } from "../../controls/components/EntitiesSection/utils";
import ScenarioSetEdge from "./ScenarioSetEdge";
import ScenarioSetLegendSidebar from "./ScenarioSetLegendSidebar";
import ScenarioSetNode from "./ScenarioSetNode";
import SingleScenariosPages from "./SingleScenariosPages";

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const nodeWidth = 130;
const nodeHeight = 40;

const nodeTypes = { customNode: ScenarioSetNode };
const edgeTypes = { customEdge: ScenarioSetEdge };

const ScenarioSetFullFlow = ({
  scenarioSet,
  fetchedSteps,
  stepsFetchIsLoading,
  width = null,
  height = null,
  selectedAsset,
  logic,
}) => {
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const paperWidth = width || 400;
  const paperHeight = height || 200;
  const [loading, setLoading] = useState(true);
  const colors = useColors();
  const [nodesAreLoading, setNodesAreLoading] = useState(true);
  const [layoutFinished, setLayoutFinished] = useState(false);
  const [key, setKey] = useState(0);
  const location = useLocation();
  const [scenarioSetId, setScenarioSetId] = useState(null);
  const reactFlowRef = useRef(null);
  const { trackEvent } = useAPI();
  const { flattenEntities } = useEntities();
  const [pageScenarios, setPageScenarios] = useState(0);
  const NUMBER_OF_SCENARIOS_PER_PAGE = 5;
  const indirectEdgeTooltip = `This step threatens "${selectedAsset?.name}" because an entity at risk has access to one of its entities.`;

  // Switches variables
  const [isMitigatedShown, setIsMitigatedShown] = useState(false);
  const [isMitigatedDisabled, setIsMitigatedDisabled] = useState(false);
  const [isMitigatedDisabledTooltip, setIsMitigatedDisabledTooltip] =
    useState(null);
  const [isFocusedScenarioShown, setIsFocusedScenarioShown] = useState(false);
  const [isFocusedScenarioDisabled, setIsFocusedScenarioDisabled] = useState(
    !scenarioSet.has_focused
  );
  const [
    isFocusedScenarioDisabledTooltip,
    setIsFocusedScenarioDisabledTooltip,
  ] = useState(null);
  const [isActiveDetectionsShown, setIsActiveDetectionsShown] = useState(false);
  const [isActiveDetectionsDisabled, setIsActiveDetectionsDisabled] =
    useState(false);
  const [
    isActiveDetectionsDisabledTooltip,
    setIsActiveDetectionsDisabledTooltip,
  ] = useState("");

  // Scroll buttons variables
  const [isLeftScrollButtonDisabled, setIsLeftScrollButtonDisabled] =
    useState(true);
  const [isRightScrollButtonDisabled, setIsRightScrollButtonDisabled] =
    useState(false);
  const [areScrollButtonsVisible, setAreScrollButtonsVisible] = useState(false);

  // Focused entities variables
  const [focusedEntitiesIds, setFocusedEntitiesIds] = useState([]);
  const [focusedEntitiesBoxWidth, setFocusedEntitiesBoxWidth] =
    useState(paperWidth);
  const focusedEntitiesBoxRef = useRef(null);
  const [isFocusedEntityDrawerOpen, setIsFocusedEntityDrawerOpen] =
    useState(false);
  const [clickedFocusedEntity, setClickedFocusedEntity] = useState(null);
  const [clickedEntityUniqueId, setClickedEntityUniqueId] = useState(null);
  const {
    fetchFocusedScenarios,
    getAllDetectedStepIDs,
    fetchSingleScenariosByScenarioSet,
  } = useAPI();

  const { data: detectedStepIDs } = useQuery({
    queryKey: stepKeys.detectedSteps(),
    enabled: detectionsActive !== false,
    queryFn: async () => {
      try {
        const response = await getAllDetectedStepIDs();
        return response.data;
      } catch (err) {
        return [];
      }
    },
  });

  useEffect(() => {
    if (detectedStepIDs && fetchedSteps) {
      let hasDetectedSteps = false;
      for (let step in fetchedSteps) {
        if (detectedStepIDs.includes(Number(step))) {
          hasDetectedSteps = true;
          break;
        }
      }
      setIsActiveDetectionsDisabled(!hasDetectedSteps);
      if (!hasDetectedSteps) {
        setIsActiveDetectionsDisabledTooltip("No detected Steps");
      }
    }
  }, [detectedStepIDs, fetchedSteps]);

  // It's required to solve ResizeObserver loop limit exceeded error
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [scenarioSet]);

  useEffect(() => {
    setScenarioSetId(scenarioSet.locations_and_phases_and_asset);

    if (scenarioSet?.coverage === 0) {
      setIsMitigatedShown(false); // Show non mitigated scenarios by default
      setIsMitigatedDisabled(true); // Disable switch
      setIsMitigatedDisabledTooltip("All scenarios are high priority");
    } else if (scenarioSet?.coverage === 100) {
      setIsMitigatedShown(true); // Show mitigated scenarios by default
      setIsMitigatedDisabled(true); // Disable switch
      setIsMitigatedDisabledTooltip("No high priority scenarios to show");
    } else {
      // Enable switch
      setIsMitigatedShown(false);
      setIsMitigatedDisabled(false);
      setIsMitigatedDisabledTooltip(null);
    }
  }, []);

  const { regularAndActiveSmartAsset: assets, isLoadingAll: assetsIsLoading } =
    useSmartAndRegularAssets();

  const { data: focusedScenarios, isLoading: focusedScenariosIsLoading } =
    useQuery({
      queryKey: scenarioKeys.scenarioByScenarioSetId(scenarioSetId),
      queryFn: async () => {
        return await fetchFocusedScenarios(scenarioSetId);
      },
      select: (response) => response.data,
      enabled: scenarioSetId !== null,
    });

  const { data: singleScenarios, isLoading: singleScenariosIsLoading } =
    useQuery({
      queryKey: scenarioKeys.singleScenarioByScenarioSetId(scenarioSetId),
      queryFn: async () => {
        return await fetchSingleScenariosByScenarioSet(scenarioSetId);
      },
      select: (response) => response.data,
      enabled: scenarioSetId !== null,
    });

  const { fetchEntitiesBatch } = useAPI();

  const { data: focusedComplexEntities } = useQuery({
    queryKey: entityKeys.entitiesByScenarioSet(scenarioSetId),
    queryFn: async () => {
      let entities = (
        await fetchEntitiesBatch(
          focusedEntitiesIds,
          "scenarioSet-" + scenarioSetId
        )
      ).data;
      return flattenEntities(entities);
    },
    select: (response) => response,
    enabled: focusedEntitiesIds.length > 0,
  });

  // Handle changes in fetched focused scenarios
  useEffect(() => {
    if (focusedScenarios !== null) {
      if (focusedScenarios?.length === 0) {
        setIsFocusedScenarioDisabledTooltip(
          "No entity-based scenarios to show"
        );
      } else if (focusedScenarios?.length === scenarioSet?.scenario_count) {
        setIsFocusedScenarioShown(false);
        setIsFocusedScenarioDisabled(true);
        setIsFocusedScenarioDisabledTooltip("All scenarios are entity-based");
      }

      const allFocusedEntities = new Set(
        focusedScenarios?.reduce((acc, scenario) => {
          return acc.concat(scenario.involved_entity_ids);
        }, [])
      );
      setFocusedEntitiesIds(Array.from(allFocusedEntities));
    }
  }, [focusedScenarios]);

  useEffect(() => {
    const container = focusedEntitiesBoxRef.current;
    if (container) {
      setAreScrollButtonsVisible(container.scrollWidth > container.clientWidth);
    }
  }, [isFocusedScenarioShown, focusedEntitiesBoxRef.current]);

  useEffect(() => {
    setNodesAreLoading(true);
    setNodes([]);
    setEdges([]);
    setLayoutFinished(false);
    setKey((prevKey) => prevKey + 1); // Update the key to force a re-render
  }, [
    isMitigatedShown,
    isFocusedScenarioShown,
    isActiveDetectionsShown,
    logic,
  ]);

  const indirectStepToAssetConnection = (step) => {
    if (!step.misconfigured_entity_ids || !selectedAsset.entity_ids) {
      return false;
    }

    const hasCommonItem = step.misconfigured_entity_ids.some((id) =>
      selectedAsset.entity_ids.includes(id)
    );

    return !hasCommonItem;
  };

  const setNodeEntityTypeFilter = (node, prev_step) => {
    const misconfiguredEntityIds = prev_step.misconfigured_entity_ids || [];
    const hasHost = misconfiguredEntityIds.some(
      (entity_id) =>
        extractTypeFromEntityId(entity_id) === ComplexEntityTypes.HOST
    );
    const hasServer = misconfiguredEntityIds.some(
      (entity_id) =>
        extractTypeFromEntityId(entity_id) === ComplexEntityTypes.SERVER
    );

    // If the previous step has only hosts, show only hosts for the current step (same for servers)
    if (hasHost && !hasServer) {
      node.entity_type_filter = ComplexEntityTypes.HOST;
    } else if (!hasHost && hasServer) {
      node.entity_type_filter = ComplexEntityTypes.SERVER;
    }
  };

  const createMergedNodesFromLocationsPhasesAndAsset = (
    scenarioSet,
    isMitigatedShown,
    isFocusedScenarioShown,
    isActiveDetectionsShown,
    detectedStepIDs
  ) => {
    const tempNodes = [];
    const location_node_ids = [];

    if (scenarioSet.coverage === 100 && !isMitigatedShown) {
      // if all scenarios are mitigated and the switch is off to show non-mitigated scenarios
      return tempNodes;
    }
    for (let i = 0; i < scenarioSet.locations_in_order.length; i++) {
      // node for location
      tempNodes.push({
        id: `${scenarioSet.locations_in_order[i]}-${i}`,
        type: "location",
        value: scenarioSet.locations_in_order[i].toString(),
        position: "middle",
      });
      location_node_ids.push(`${scenarioSet.locations_in_order[i]}-${i}`);
    }

    if (scenarioSet.phases_info) {
      let previousIsVirtual = false;
      scenarioSet.phases_info.forEach((phaseInfo, phaseInfoIndex) => {
        // Copy phaseInfo to avoid modifying the original object
        let phaseInfoCopy = { ...phaseInfo };
        // return phase with detected steps
        const phasesWithDetected = phaseInfo.phase_ids.filter((phase) => {
          // Split out step IDs
          const steps = phase.split("-");
          steps.shift();
          // Return true if any step is in detectedStepIDs
          return steps.some((stepId) =>
            detectedStepIDs?.includes(Number(stepId))
          );
        });

        if (isActiveDetectionsShown) {
          // Keep only phases that have a detected step, unless none have any detected step
          if (phasesWithDetected.length > 0) {
            phaseInfoCopy.phase_ids = phasesWithDetected;
          }
          // If phasesWithDetected is empty, do nothing so all phase_ids remain
        }

        // for each phase, create a node for each step in the phase and connect them to each other
        // and to the start and end of the phase
        phaseInfoCopy.phase_ids.forEach((phase, phaseIndex) => {
          let steps = phase.split("-");
          // pop first step as it is the phase type i.e: I (Infiltration)
          steps.shift();
          // Count the number of pushed phase nodes to remove when a step is mitigated
          let pushedPhaseNodesCounter = 0;
          for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
            const step_id = steps[stepIndex];
            const step_data = fetchedSteps[step_id];
            if (!step_data) {
              return;
            }
            if (
              (step_data.is_mitigated &&
                step_data.protection_level !==
                  ProtectionLevelText.RISK_ACCEPTED &&
                !isMitigatedShown) ||
              (isFocusedScenarioShown &&
                !focusedScenarios?.some((scenario) =>
                  scenario.steps.includes(step_data.step_id)
                ))
            ) {
              // if the step does not meet the filter criteria
              // do not show the step
              // remove other nodes that were added as part of this phase
              for (let j = 0; j < pushedPhaseNodesCounter; j++) {
                tempNodes.pop();
              }
              break;
            }

            const suffix = "";
            if (step_data.is_virtual) {
              previousIsVirtual = true;
            } else {
              const node = {
                position: "middle",
                value: step_data,
                type: "step",
                id: `${step_id};${suffix}`,
              };

              if (stepIndex === 0 && steps.length === 1) {
                // if there is only one step in the phase
                // connect the step to the start and end of the phase

                if (phaseInfoIndex === scenarioSet.phases_info.length - 1) {
                  // if it is the last phase
                  // connect the end of the phase to the asset
                  node.start = location_node_ids[phaseInfoIndex];
                  node.end = scenarioSet.asset_id;
                  if (indirectStepToAssetConnection(step_data)) {
                    node.indirectAssetConnection = true;
                  }
                } else {
                  // if it is not the last phase
                  // connect the end of the phase to the start of the next phase
                  node.start = location_node_ids[phaseInfoIndex];
                  node.end = location_node_ids[phaseInfoIndex + 1];
                }
              } else if (stepIndex === 0 && steps.length > 1) {
                // if there are multiple steps in the phase
                // connect the first step to the start of the phase
                // and the last step to the end of the phase
                node.start = location_node_ids[phaseInfoIndex];
                node.end = steps[stepIndex + 1].toString() + ";" + suffix;
              } else if (stepIndex < steps.length - 1) {
                // if it is not the first or last step
                // connect the step to the previous and next step
                // in the phase
                if (previousIsVirtual) {
                  // if the previous step is virtual connect to the step before the previous step
                  node.start = steps[stepIndex - 2].toString() + ";" + suffix;
                } else {
                  node.start = steps[stepIndex - 1].toString() + ";" + suffix;
                }
                node.end = steps[stepIndex + 1].toString() + ";" + suffix;
              } else if (
                phaseInfoIndex ===
                scenarioSet.phases_info.length - 1
              ) {
                // if it is the last phase and the last step in the phase
                // connect the step to the previous step in the phase
                // and the end of the phase to the asset
                if (previousIsVirtual) {
                  node.start = steps[stepIndex - 2].toString() + ";" + suffix;
                } else {
                  node.start = steps[stepIndex - 1].toString() + ";" + suffix;
                }
                node.end = scenarioSet.asset_id;

                if (indirectStepToAssetConnection(step_data)) {
                  node.indirectAssetConnection = true;
                }
              } else {
                // if it is the last step in the phase
                // connect the step to the previous step in the phase
                // and the end of the phase

                if (previousIsVirtual) {
                  node.start = steps[stepIndex - 2].toString() + ";" + suffix;
                } else {
                  node.start = steps[stepIndex - 1].toString() + ";" + suffix;
                }
                node.end = location_node_ids[phaseInfoIndex + 1];
              }

              // Set the entity type filter for vulnerability nodes - to show only the relevant entity types (e.g. don't show servers for cwe step if the prev step has only hosts)
              if (step_data?.cwe_id) {
                const prev_step_id = steps[stepIndex - 1];
                const prev_step = fetchedSteps[prev_step_id];
                if (prev_step) {
                  setNodeEntityTypeFilter(node, prev_step);
                }
              }

              tempNodes.push(node);
              pushedPhaseNodesCounter++;
              previousIsVirtual = false;
            }
          }
        });
      });
    }

    const asset = assets?.filter((asset) => {
      return asset?.asset_id === scenarioSet.asset_id;
    })[0];

    // node for asset
    tempNodes.push({
      id: scenarioSet.asset_id,
      type: "asset",
      value: {
        name: asset?.name,
        type: asset?.type,
        id: scenarioSet.asset_id,
      },
      end: "execution",
      position: "middle",
    });

    return tempNodes;
  };

  const nodesAndEdgesGetter = (
    isMitigatedShown,
    isFocusedScenarioShown,
    isActiveDetectionsShown,
    detectedStepIDs
  ) => {
    const nodesAndEdges = { nodes: [], edges: [] };
    let mergedNodes = [];

    mergedNodes = createMergedNodesFromLocationsPhasesAndAsset(
      scenarioSet,
      isMitigatedShown,
      isFocusedScenarioShown,
      isActiveDetectionsShown,
      detectedStepIDs
    );

    const scenarioSetNodes = mergedNodes?.map((node) => ({
      id: node.id.toString(),
      data: {
        nodeLocation: node.position,
        nodeValue: node.value,
        type: node.type,
        start: node.start,
        end: node.end,
        entity_type_filter: node?.entity_type_filter,
        graphSource: "scenarioSetGraph",
        ...(node?.indirectAssetConnection === true
          ? { tooltip: indirectEdgeTooltip }
          : {}),
      },
    }));

    const scenarioSetEdges = [];
    // for every node, there is a start and end node
    // except for the asset node which only has a start node
    // for every node, create an edge that connects it to the start and end nodes
    // if they exist
    for (let i = 0; i < mergedNodes.length; i++) {
      const location = mergedNodes[i]?.id.toString();
      const startLocation = mergedNodes[i]?.start;
      const endLocation = mergedNodes[i]?.end;
      if (startLocation) {
        const edge = {
          id: `e${startLocation}-${location}`,
          source: startLocation,
          target: location,
          markerEnd: {
            type: MarkerType.Arrow,
            width: 20,
            height: 20,
            color: colors.primary_32percent,
          },
        };
        scenarioSetEdges.push(edge);
      }
      if (endLocation) {
        const edge = {
          id: `e${location}-${endLocation}`,
          source: location,
          target: endLocation,
          markerEnd: {
            type: MarkerType.Arrow,
            width: 10,
            height: 10,
            color: colors.primary_32percent,
          },
          style:
            mergedNodes[i]?.indirectAssetConnection === true
              ? { strokeDasharray: "5,5" } // Dashed line
              : {},
          data:
            mergedNodes[i]?.indirectAssetConnection === true
              ? { tooltip: indirectEdgeTooltip }
              : {},
        };
        scenarioSetEdges.push(edge);
      }
    }
    let scenarioSetNodesSet = new Set(scenarioSetNodes.map((node) => node.id));
    const uniqueScenarioSetNodes = Array.from(scenarioSetNodesSet).map((id) =>
      scenarioSetNodes.find((node) => node.id === id)
    );
    let scenarioSetEdgesSet = new Set(scenarioSetEdges.map((edge) => edge.id));
    const uniqueScenarioSetEdges = Array.from(scenarioSetEdgesSet).map((id) =>
      scenarioSetEdges.find((edge) => edge.id === id)
    );

    nodesAndEdges.nodes.push(...uniqueScenarioSetNodes);
    nodesAndEdges.edges.push(...uniqueScenarioSetEdges);

    return nodesAndEdges;
  };

  useEffect(() => {
    if (nodes.length > 0 && !nodesAreLoading) {
      const dagreGraph = new dagre.graphlib.Graph();
      dagreGraph.setDefaultEdgeLabel(() => ({}));
      dagreGraph.setGraph({ rankdir: "LR" });

      nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
      });

      edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
      });

      dagre.layout(dagreGraph);
      const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);

        if (nodeWithPosition) {
          node.position = {
            x: nodeWithPosition.x - nodeWidth / 2,
            y: nodeWithPosition.y - nodeHeight / 2,
          };
        }
        return node;
      });

      setNodes(layoutedNodes);
      setLayoutFinished(true);
    }
  }, [nodesAreLoading]);

  useEffect(() => {
    if (scenarioSet && fetchedSteps && assetsIsLoading === false) {
      if (!focusedScenariosIsLoading) {
        const result = nodesAndEdgesGetter(
          isMitigatedShown,
          isFocusedScenarioShown,
          isActiveDetectionsShown,
          detectedStepIDs
        );
        const nodesWithTypes = result.nodes.map((node) => ({
          ...node,
          type: "customNode",
        }));

        const edgesWithTypes = result.edges.map((edge, index) => {
          if (index === result.edges.length - 1) {
            return {
              ...edge,
              style: {
                ...edge.style, // Merge existing style
                stroke: colors.primary_32percent,
                strokeOpacity: 0.7,
                strokeWidth: 1,
                width: 1,
              },
              type: "customEdge",
              data: edge.data,
            };
          } else {
            return {
              ...edge,
              style: {
                ...edge.style, // Merge existing style
                stroke: colors.grey_300,
                strokeWidth: 2,
              },
              type: "customEdge",
              data: edge.data,
            };
          }
        });

        setNodes(nodesWithTypes);
        setEdges(edgesWithTypes);
      }
    }
  }, [
    scenarioSet,
    fetchedSteps,
    isMitigatedShown,
    isFocusedScenarioShown,
    focusedScenariosIsLoading,
    assetsIsLoading,
    isActiveDetectionsShown,
    detectedStepIDs,
    logic,
  ]);

  useEffect(() => {
    if (nodes.length > 0 && edges.length > 0) {
      setNodesAreLoading(false);
    } else if (nodes.length === 1) {
      setNodesAreLoading(false);
    } else {
      setNodesAreLoading(true);
    }
  }, [nodes, edges]);

  const handleFocusedEntitiesScroll = (direction) => {
    const container = document.getElementById("focusedEntitiesBox");
    container.scrollLeft += direction * 100;

    if (container.scrollLeft <= 0) {
      setIsLeftScrollButtonDisabled(true);
    } else {
      setIsLeftScrollButtonDisabled(false);
    }

    if (container.scrollLeft >= container.scrollWidth - container.clientWidth) {
      setIsRightScrollButtonDisabled(true);
    } else {
      setIsRightScrollButtonDisabled(false);
    }
  };

  const handleFocusedEntitiesWheel = (event) => {
    if (event.deltaY === 0 && event.deltaX !== 0) {
      const container = document.getElementById("focusedEntitiesBox");

      if (container.scrollLeft <= 0) {
        setIsLeftScrollButtonDisabled(true);
      } else {
        setIsLeftScrollButtonDisabled(false);
      }

      if (
        container.scrollLeft >=
        container.scrollWidth - container.clientWidth
      ) {
        setIsRightScrollButtonDisabled(true);
      } else {
        setIsRightScrollButtonDisabled(false);
      }
    }
  };

  // Set the width of the focused entities box according to the react flow graph width
  useEffect(() => {
    const reactFlow = reactFlowRef.current;
    if (reactFlow) {
      setFocusedEntitiesBoxWidth(reactFlow.offsetWidth);
    }
  }, [isFocusedScenarioShown]);

  const focusedEntityOnClick = (entityType, entityUniqueId) => {
    trackEvent(
      `User clicked on focused entity ${entityType}-${entityUniqueId}, from scenario set modal`
    );
    setClickedEntityUniqueId(entityUniqueId);
  };

  // Open entity drawer when a focused entity is clicked
  useEffect(() => {
    if (clickedEntityUniqueId && focusedComplexEntities?.length > 0) {
      const focusedEntity = focusedComplexEntities?.find(
        (entity) => entity.unique_id === clickedEntityUniqueId
      );

      setClickedFocusedEntity(focusedEntity);
      setIsFocusedEntityDrawerOpen(true);
      // Set to null to let the useEffect be called again when the same entity is clicked
      setClickedEntityUniqueId(null);
    }
  }, [focusedComplexEntities, clickedEntityUniqueId]);

  const EntityIcon = ({ iconName, sx }) => {
    const IconComponent = iconMapping[iconName];

    if (!IconComponent) {
      console.warn(`Icon "${iconName}" is not defined in iconMapping.`);
      return null;
    }

    return <SvgToIcon IconLogo={IconComponent} width="18px" style={sx} />;
  };
  const filteredScenarios = useMemo(() => {
    let scenarios = singleScenarios;
    if (!isMitigatedShown) {
      scenarios = singleScenarios?.filter((scenario) => {
        // Show scenarios that are not effectively mitigated (excluding RISK_ACCEPTED steps)
        return !isScenarioEffectivelyMitigated(scenario, fetchedSteps);
      });
    }
    if (isFocusedScenarioShown) {
      scenarios = scenarios?.filter((scenario) => {
        if (isFocusedScenarioShown && scenario.is_focused) {
          return true;
        }
      });
    }
    if (isActiveDetectionsShown) {
      scenarios = scenarios?.filter((scenario) => {
        if (
          isActiveDetectionsShown &&
          scenario.steps.some((step) => detectedStepIDs?.includes(step))
        ) {
          return true;
        }
        return false;
      });
    }

    // Sort scenarios: 1) non-mitigated, 2) risk-accepted, 3) effectively mitigated
    return scenarios?.sort((a, b) => {
      const aEffectivelyMitigated = isScenarioEffectivelyMitigated(
        a,
        fetchedSteps
      );
      const bEffectivelyMitigated = isScenarioEffectivelyMitigated(
        b,
        fetchedSteps
      );

      // Assign priority values: lower number = higher priority
      const getPriority = (scenario, effectivelyMitigated) => {
        if (effectivelyMitigated) return 3;
        if (scenario.is_mitigated) return 2; // Risk-accepted
        return 1;
      };

      const aPriority = getPriority(a, aEffectivelyMitigated);
      const bPriority = getPriority(b, bEffectivelyMitigated);

      return aPriority - bPriority;
    });
  }, [
    singleScenarios,
    isMitigatedShown,
    isFocusedScenarioShown,
    isActiveDetectionsShown,
    detectedStepIDs,
  ]);

  return (
    <Box
      sx={{
        pb: "4px",
      }}
    >
      <Box minHeight={"40px"}>
        {isFocusedScenarioShown && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              maxWidth: paperWidth * 0.85,
              zIndex: 500,
            }}
          >
            {areScrollButtonsVisible && (
              <Button
                onClick={() => handleFocusedEntitiesScroll(-1)}
                disabled={isLeftScrollButtonDisabled}
              >
                <ChevronLeft />
              </Button>
            )}
            <Box
              id="focusedEntitiesBox"
              ref={focusedEntitiesBoxRef}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                overflowX: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
              onWheel={handleFocusedEntitiesWheel}
            >
              {focusedEntitiesIds.map((entityId, index) => {
                const firstDashIndex = entityId.indexOf("-");
                const entityType = entityId.substring(0, firstDashIndex);
                const entityUniqueId = entityId.substring(firstDashIndex + 1);
                const isComplexEntity = isComplexEntityType(entityType);
                const focusedEntity = focusedComplexEntities?.find(
                  (entity) => entity.unique_id === entityUniqueId
                );
                const displayName = isComplexEntity
                  ? focusedEntity?.display_name
                  : entityUniqueId;

                return (
                  <React.Fragment key={index}>
                    <Button
                      disableRipple
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        minWidth: "160px",
                        whiteSpace: "nowrap",
                        cursor: isComplexEntity ? "pointer" : "default",
                        "&:hover": {
                          backgroundColor: isComplexEntity
                            ? colors.black_4percent
                            : "inherit", // Prevent the button from glowing on hover
                        },
                        "&:active": {
                          backgroundColor: isComplexEntity
                            ? colors.black_12percent
                            : "inherit", // Prevent the button from glowing when clicked
                        },
                      }}
                      onClick={() => {
                        if (isComplexEntity) {
                          focusedEntityOnClick(entityType, entityUniqueId);
                        }
                      }}
                    >
                      <CustomTooltip title={entityType}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          <EntityIcon
                            iconName={entityType}
                            sx={{
                              marginleft: "3px",
                              marginRight: "8px",
                              marginTop: "4px",
                              marginBottom: "4px",
                              width: "18px",
                              height: "18px",
                            }}
                          />
                        </Box>
                      </CustomTooltip>
                      <CustomTooltip title={displayName}>
                        <Typography
                          variant="caption"
                          sx={{
                            marginRight: "4px",
                            marginTop: "4px",
                            marginBottom: "4px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textTransform: "none",
                          }}
                        >
                          {displayName}
                        </Typography>
                      </CustomTooltip>
                    </Button>
                    {index < focusedEntitiesIds.length - 1 && (
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          alignSelf: "center",
                          backgroundColor: colors.grey_400,
                          height: "24px",
                          marginRight: "8px",
                          marginLeft: "8px",
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
            {areScrollButtonsVisible && (
              <Button
                onClick={() => handleFocusedEntitiesScroll(1)}
                disabled={isRightScrollButtonDisabled}
              >
                <ChevronRight />
              </Button>
            )}
          </Box>
        )}
        <EntityDrawer
          openDrawer={isFocusedEntityDrawerOpen && clickedFocusedEntity}
          selectedEntity={clickedFocusedEntity}
          onClose={() => {
            setIsFocusedEntityDrawerOpen(false);
          }}
        />
      </Box>
      <Box sx={{ display: "flex" }}>
        {logic === ScenariosModalStateLogic.UnifiedPaths ? (
          <Box sx={{ width: paperWidth, height: paperHeight }}>
            {/* React flow requires wrapper object with width and height */}
            {!loading && !stepsFetchIsLoading && layoutFinished ? (
              <ReactFlow
                ref={reactFlowRef}
                key={key}
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView
                style={{
                  backgroundColor: colors.grey_2percent,
                  border: `1px solid ${colors.grey_32percent}`,
                  borderRadius: "10px",
                }}
                proOptions={{ hideAttribution: true }}
                nodesConnectable={false}
              ></ReactFlow>
            ) : (
              <LoadingIcon />
            )}
          </Box>
        ) : (
          <Box sx={{ width: paperWidth, height: paperHeight }}>
            {singleScenariosIsLoading ? (
              <LoadingIcon size={16} />
            ) : (
              <SingleScenariosPages
                scenarioSet={scenarioSet}
                fetchedSteps={fetchedSteps}
                selectedAsset={selectedAsset}
                pageScenarios={pageScenarios}
                numberOfScenariosInPage={NUMBER_OF_SCENARIOS_PER_PAGE}
                height={paperHeight}
                scenarios={filteredScenarios}
              />
            )}
          </Box>
        )}
        <Box
          sx={{
            width: paperWidth / 4,
            height: paperHeight,
            marginLeft: "16px",
          }}
        >
          <ScenarioSetLegendSidebar
            isMitigatedShown={isMitigatedShown}
            setIsMitigatedShown={setIsMitigatedShown}
            isMitigatedDisabled={isMitigatedDisabled}
            isMitigatedDisabledTooltip={isMitigatedDisabledTooltip}
            isFocusedScenarioShown={isFocusedScenarioShown}
            setIsFocusedScenarioShown={setIsFocusedScenarioShown}
            isFocusedScenarioDisabled={isFocusedScenarioDisabled}
            isFocusedScenarioDisabledTooltip={isFocusedScenarioDisabledTooltip}
            isActiveDetectionsShown={isActiveDetectionsShown}
            setIsActiveDetectionsShown={setIsActiveDetectionsShown}
            isActiveDetectionsDisabled={isActiveDetectionsDisabled}
            isActiveDetectionsDisabledTooltip={
              isActiveDetectionsDisabledTooltip
            }
          />
        </Box>
      </Box>
      {logic === ScenariosModalStateLogic.SinglePaths && (
        <Box
          display="flex"
          flexDirection={"row"}
          alignItems="center"
          mt={"4px"}
          gap={3}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: colors.primary_main,
            }}
          >
            Attack Paths
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              alignItems: "center",
            }}
          >
            <IconButton
              disabled={pageScenarios === 0}
              onClick={() => setPageScenarios(pageScenarios - 1)}
            >
              <ChevronLeftRounded />
            </IconButton>
            <Typography variant="caption">
              Showing {pageScenarios * NUMBER_OF_SCENARIOS_PER_PAGE + 1}-
              {pageScenarios * NUMBER_OF_SCENARIOS_PER_PAGE +
                NUMBER_OF_SCENARIOS_PER_PAGE >
              filteredScenarios?.length
                ? filteredScenarios?.length
                : pageScenarios * NUMBER_OF_SCENARIOS_PER_PAGE +
                  NUMBER_OF_SCENARIOS_PER_PAGE}{" "}
              of {filteredScenarios?.length}
            </Typography>
            <IconButton
              onClick={() => setPageScenarios(pageScenarios + 1)}
              disabled={
                pageScenarios * NUMBER_OF_SCENARIOS_PER_PAGE +
                  NUMBER_OF_SCENARIOS_PER_PAGE >=
                filteredScenarios?.length
              }
            >
              <ChevronRightRounded />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ScenarioSetFullFlow;
